import React, { memo, useMemo } from 'react';
import { Typography } from '@ublend-npm/aulaui-core';
import { useSelector } from 'react-redux';
import { selectors as reactionSelectors } from '@app/store/reactions';
import { Post } from '@app/types/core';
import {
  accessingCurrentSpaceAsStaff,
  isInstructor,
} from '@core/selectors/classroom';
import { currentUserId } from '@core/selectors/user';
import { getComments } from '@core/selectors/comments';
import { AulaState } from '@core/types/state';
import CommentsEntityTypes from '@core/utils/commentsEntityTypes';
import formatDate from '@core/utils/formatDate';
import ResponsiveEditorReadOnly from '../Shared/ResponsiveEditorReadOnly';
import PostCard from '../Shared/PostCard';
import PostReactions from './PostReactions';
import ResponsivePostTags from './ResponsivePostTags';
import ResponsivePostComments from './ResponsivePostComments';

type PostUser = Post['user'] & {
  avatar?: string;
  firstName?: string;
  lastName?: string;
};

type CommonRoomPostCardProps = {
  post: Post;
  readOnly?: boolean;
};

type GetReactionsSelector = (
  state: AulaState,
  props: { itemId: string }
) => unknown[];

const parseContent = (content: string) => {
  try {
    return JSON.parse(content);
  } catch {
    return null;
  }
};

const CommonRoomPostCard = ({
  post,
  readOnly = false,
}: CommonRoomPostCardProps) => {
  const author: PostUser = post.user;
  const getReactions = useMemo<GetReactionsSelector>(() => {
    const selector: any = reactionSelectors.getReactionsSelector();
    return (state, props) => selector(state, props);
  }, []);
  const hasReactions = useSelector<AulaState, boolean>(
    (state) => getReactions(state, { itemId: post.objectId }).length > 0
  );
  const hasComments = useSelector<AulaState, boolean>(
    (state) =>
      getComments(state, post.objectId, CommentsEntityTypes.post).length > 0
  );
  const { isEducator, shouldShowTags } = useSelector((state: AulaState) => {
    const data = state.data as any;
    const storedPost = data.posts[post.objectId] || post;
    const hasTags = (storedPost.tagPosts || []).some(
      (tagPost: { tagId: string }) => data.tags[tagPost.tagId]
    );
    const canManageTags =
      !accessingCurrentSpaceAsStaff(state) &&
      (isInstructor(state) ||
        currentUserId(state) === storedPost.user.objectId);

    return {
      isEducator: isInstructor(state, author.objectId),
      shouldShowTags: hasTags || canManageTags,
    };
  });
  const fullName =
    `${author.firstName || ''} ${author.lastName || ''}`.trim() ||
    author.email ||
    'Unknown user';
  const editorState = useMemo(() => parseContent(post.content), [post.content]);
  const shouldShowReactions = !readOnly || hasReactions;
  const shouldShowComments = !readOnly || hasComments;

  if (!post.content) return null;

  return (
    <PostCard
      ariaLabel={`Post by ${fullName}`}
      author={{
        name: fullName,
        avatarUrl: author.avatar,
        firstName: author.firstName,
        lastName: author.lastName,
        role: isEducator ? 'Educator' : undefined,
      }}
      timestamp={formatDate(post.scheduledFor || post.createdAt)}
      edited={Boolean(post.editedAt)}
      content={
        editorState ? (
          <ResponsiveEditorReadOnly
            key={post.editedAt || post.createdAt}
            itemId={post.objectId}
            itemType={post.feedType}
            editorState={editorState}
          />
        ) : (
          <Typography component="p" sx={{ m: 0 }}>
            {post.content}
          </Typography>
        )
      }
      tags={shouldShowTags ? <ResponsivePostTags post={post} /> : null}
      reactions={
        shouldShowReactions ? (
          <PostReactions postId={post.objectId} readOnly={readOnly} />
        ) : null
      }
      comments={
        shouldShowComments ? (
          <ResponsivePostComments postId={post.objectId} readOnly={readOnly} />
        ) : null
      }
    />
  );
};

export default memo(CommonRoomPostCard);
